import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  LinearProgress,
  Radio,
  RadioGroup,
  TextField,
} from '@mui/material';
import { useAppTranslation } from '../../services/i18n';
import {
  financialApproversOf,
  leadershipOf,
  targetOptionsFor,
  WorkplaceNode,
  WorkplaceTree,
} from '../../helpers/workplaceTree';
import { DeleteUsersStrategy } from '../../store/workplaces';
import { pickerInputProps, pickerSlotProps } from '../form/SelectFormField';

export interface DeleteChoice {
  includeDescendants: boolean;
  strategy: DeleteUsersStrategy;
  targetId?: number;
}

interface Props {
  tree: WorkplaceTree;
  node: WorkplaceNode;
  /** counts from the deletion_impact endpoint; undefined while they are loading */
  impact?: { organization_units_count: number; affected_users_count: number };
  isSaving: boolean;
  onCancel: () => void;
  onConfirm: (choice: DeleteChoice) => void;
}

export const WorkplaceDeleteDialog: React.FC<Props> = ({
  tree,
  node,
  impact,
  isSaving,
  onCancel,
  onConfirm,
}) => {
  const t = useAppTranslation();
  const hasChildren = node.children.length > 0;
  const [cascade, setCascade] = useState(true);
  const [strategy, setStrategy] = useState<DeleteUsersStrategy>('nullify');
  const [targetId, setTargetId] = useState<number | null>(null);

  useEffect(() => {
    setCascade(true);
    setStrategy('nullify');
    setTargetId(null);
  }, [node]);

  // the subtree is going away, so its own units cannot receive the members
  const targetOptions = useMemo(() => targetOptionsFor(tree, node), [tree, node]);

  // a leaf has no subtree to keep, so the delete always covers "everything below"
  const includeDescendants = hasChildren ? cascade : true;
  const leadership = leadershipOf(node.unit);
  const financialApprovers = financialApproversOf(node.unit);
  const hasAssignments = !!(
    leadership.leaders.length ||
    leadership.deputy ||
    financialApprovers.leaders.length ||
    financialApprovers.deputy
  );
  const canMove = includeDescendants && strategy === 'reassign';
  const isValid = !canMove || !!targetId;

  return (
    <Dialog open={true} onClose={onCancel} maxWidth={'sm'} fullWidth={true}>
      <DialogTitle>{t('workplaces.delete.title')}</DialogTitle>
      <LinearProgress hidden={!isSaving && !!impact} />
      <DialogContent>
        <p>
          {t(hasChildren && cascade ? 'workplaces.delete.leadSubtree' : 'workplaces.delete.lead', {
            name: node.unit.name,
          })}
        </p>

        {/* the impact endpoint always answers for the whole subtree, so its numbers may be
            used only when the whole subtree really goes; for a single unit the count of
            members is the one number that can be stated exactly, and the leadership that
            ends with it is said in words instead of being guessed into that number */}
        <p className={'workplace-delete__impact'}>
          {!includeDescendants
            ? t('workplaces.delete.impactUnit', { members: node.userCount })
            : impact
            ? t('workplaces.delete.impact', {
                units: impact.organization_units_count,
                users: impact.affected_users_count,
              })
            : t('workplaces.delete.impactLoading')}
        </p>
        {!includeDescendants && hasAssignments && (
          <p className={'workplace-delete__impact'}>{t('workplaces.delete.impactLeadership')}</p>
        )}

        {hasChildren && (
          <>
            <h4 className={'workplace-delete__question'}>{t('workplaces.delete.childrenTitle')}</h4>
            <RadioGroup
              value={cascade ? 'cascade' : 'keep'}
              onChange={(event) => setCascade(event.target.value === 'cascade')}
            >
              <FormControlLabel
                value={'cascade'}
                control={<Radio />}
                label={t('workplaces.delete.childrenCascade')}
              />
              <FormControlLabel
                value={'keep'}
                control={<Radio />}
                label={t('workplaces.delete.childrenKeep')}
              />
            </RadioGroup>
          </>
        )}

        <h4 className={'workplace-delete__question'}>{t('workplaces.delete.usersTitle')}</h4>
        {includeDescendants ? (
          <>
            <RadioGroup
              value={strategy}
              onChange={(event) => setStrategy(event.target.value as DeleteUsersStrategy)}
            >
              <FormControlLabel
                value={'nullify'}
                control={<Radio />}
                label={t('workplaces.delete.usersNullify')}
              />
              <FormControlLabel
                value={'reassign'}
                control={<Radio />}
                label={t('workplaces.delete.usersMove')}
              />
            </RadioGroup>
            {strategy === 'reassign' && (
              <Autocomplete
                options={targetOptions}
                value={targetOptions.find((option) => option.id === targetId) ?? null}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onChange={(_, option) => setTargetId(option?.id ?? null)}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    <span>
                      {option.label}
                      {!!option.path && (
                        <span className={'workplace-detail__option-path'}> {option.path}</span>
                      )}
                    </span>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    {...pickerInputProps}
                    slotProps={pickerSlotProps(params)}
                    variant={'standard'}
                    label={t('workplaces.delete.moveTarget')}
                    required={true}
                    error={!isValid}
                  />
                )}
              />
            )}
          </>
        ) : (
          // without the cascade the backend just drops this unit's memberships
          <p className={'workplace-detail__muted'}>{t('workplaces.delete.usersKeepChildren')}</p>
        )}
      </DialogContent>
      <DialogActions>
        <Button color={'inherit'} onClick={onCancel} disabled={isSaving}>
          {t('form.cancel')}
        </Button>
        <Button
          color={'error'}
          variant={'contained'}
          disabled={!isValid || isSaving}
          onClick={() =>
            onConfirm({
              includeDescendants,
              strategy,
              targetId: canMove && targetId ? targetId : undefined,
            })
          }
        >
          {t('form.delete')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
